import type { ExceptionContext, ExceptionMechanism, ExceptionReport } from '@belvdr/shared'
import { createDeduplicator } from './dedupe.js'
import type { ExtraitDecision } from './extrait-recorder.js'
import type { ParsedException } from './exceptions.js'
import { cleanPageUrl } from './network-url.js'
import type { Sender } from './sender.js'
import type { TransportResponse } from './transport.js'
import { uuidv7 } from './uuid.js'
import { SDK_VERSION } from './version.js'

/** Un appel de la pile d'une Exception, du plus proche de l'erreur au plus lointain. */
export type BelvdrExceptionFrame = {
  function?: string | undefined
  /** URL du script. */
  file?: string | undefined
  line?: number | undefined
  column?: number | undefined
}

/** Une Exception telle que la reçoit `beforeSendException`. */
export type BelvdrException = {
  /** Type de l'erreur (`TypeError`…), `Error` pour une valeur jetée qui n'en est pas une. */
  type: string
  message: string
  frames: BelvdrExceptionFrame[]
  /** Erreur attrapée par l'application (`captureException`) ; sinon, non gérée. */
  handled: boolean
  mechanism: ExceptionMechanism
  /** URL de la page, avant le masquage automatique des paramètres sensibles. */
  url: string
  /** Instant de l'Exception, en ms depuis l'epoch. */
  timestamp: number
}

/**
 * Réécrit une Exception avant son envoi (son type, son message, ses frames, son URL), ou
 * renvoie `null` pour l'écarter. Les paramètres sensibles de l'URL sont masqués après ce hook.
 */
export type BeforeSendException = (exception: BelvdrException) => BelvdrException | null

/** Une Exception survenue dans la page, avant les garde-fous. */
export type Occurrence = {
  exception: ParsedException
  timestamp: number
  /** URL de la page, telle quelle. */
  url: string
  distinctId: string
  /** Utilisateur du navigateur, s'il est identifié : l'API lui rattache le navigateur. */
  userId?: string
  /** Session complète en cours, si elle est active. */
  session?: { id: string; windowId: string }
}

/** Message d'une Exception masqué par `maskConsole`, comme les arguments de la console. */
const MASKED_MESSAGE = '***'
/** Limites du contrat d'ingestion (cf. `@belvdr/shared`). */
const MAX_TYPE_LENGTH = 256
const MAX_MESSAGE_LENGTH = 10_000
const MAX_FRAMES = 50
const MAX_FUNCTION_LENGTH = 512
const MAX_FILE_LENGTH = 4096

/** Frame renvoyé par le hook, ramené à ce que l'API accepte (`null` s'il est inutilisable). */
function frameOf(value: unknown): BelvdrExceptionFrame | null {
  if (typeof value !== 'object' || value === null) return null
  const { function: name, file, line, column } = value as Record<string, unknown>
  const frame: BelvdrExceptionFrame = {}
  if (typeof name === 'string' && name !== '') frame.function = name.slice(0, MAX_FUNCTION_LENGTH)
  if (typeof file === 'string' && file !== '') frame.file = file.slice(0, MAX_FILE_LENGTH)
  if (Number.isInteger(line) && (line as number) >= 0) frame.line = line as number
  if (Number.isInteger(column) && (column as number) >= 0) frame.column = column as number
  return frame.function === undefined && frame.file === undefined ? null : frame
}

/** Ce que le hook a renvoyé, ramené à ce que l'API accepte ; le reste vient de l'original. */
function rewritten(result: BelvdrException, original: BelvdrException) {
  const type =
    typeof result.type === 'string' && result.type.trim() !== ''
      ? result.type.slice(0, MAX_TYPE_LENGTH)
      : original.type
  const message =
    typeof result.message === 'string' ? result.message.slice(0, MAX_MESSAGE_LENGTH) : ''
  const frames = Array.isArray(result.frames)
    ? result.frames
        .map(frameOf)
        .filter((frame) => frame !== null)
        .slice(0, MAX_FRAMES)
    : []
  const url = typeof result.url === 'string' ? result.url : original.url
  return { type, message, frames, url }
}

/**
 * Fichier d'un frame, nettoyé comme l'URL de la page : celui d'un script inline est l'URL de la
 * page elle-même, paramètres compris. Un fichier qui n'est pas une URL est gardé tel quel.
 */
function cleanFrame(frame: BelvdrExceptionFrame): BelvdrExceptionFrame {
  if (frame.file === undefined) return frame
  try {
    new URL(frame.file)
  } catch {
    return frame
  }
  return { ...frame, file: cleanPageUrl(frame.file) }
}

/**
 * Décision pour l'Extrait d'une Exception, d'après la réponse de l'API : `skip` pour une Issue
 * ignorée, ou une Exception refusée ou perdue (rien à illustrer) ; `send` sinon, y compris pour
 * une API qui ne répond pas encore `{ extrait }`.
 */
function extraitDecision(response: TransportResponse | null): ExtraitDecision {
  if (!response || response.status >= 300) return 'skip'
  const decision = (response.body as { extrait?: unknown } | undefined)?.extrait
  return decision === 'skip' ? 'skip' : 'send'
}

function warn(text: string, error?: unknown) {
  if (error === undefined) console.warn(`[belvdr] ${text}`)
  else console.warn(`[belvdr] ${text}`, error)
}

export type ExceptionReporter = {
  /** Une Exception de la page : dédoublonnée, passée au hook, puis envoyée. */
  report(occurrence: Occurrence): void
  /** Envoie aussitôt les occurrences écartées en attente (arrêt, page masquée ou fermée). */
  flush(): void
}

/**
 * Garde-fous appliqués à chaque Exception, dans le navigateur, avant tout envoi :
 * 1. dédoublonnage (même type, message, pile et origine : au plus une fois toutes les 10 s, avec le
 *    nombre d'occurrences écartées) ;
 * 2. `beforeSendException`, qui peut la réécrire ou l'écarter (un hook qui lève une erreur
 *    l'écarte, avec un avertissement) ;
 * 3. masquage des paramètres sensibles de l'URL et des fichiers de la pile, et du message avec
 *    `maskConsole`.
 */
export function createExceptionReporter(options: {
  publicKey: string
  sender: Sender
  maskMessage: boolean
  beforeSend: BeforeSendException | undefined
  context: () => ExceptionContext
  /** L'API refuse l'origine de la page : plus rien ne part. */
  refused: () => boolean
  /**
   * Extrait de l'Exception, au moment de son envoi (Mode erreurs) : son identifiant, ou
   * `undefined` sans Extrait. Une occurrence écartée par le dédoublonnage n'en a pas.
   */
  extraitFor?: (occurrence: Occurrence) => string | undefined
  /** La réponse de l'API pour l'Extrait d'une Exception envoyée : l'envoyer, ou non. */
  decideExtrait?: (extraitId: string, decision: ExtraitDecision) => void
}): ExceptionReporter {
  function send(occurrence: Occurrence, duplicateCount: number) {
    if (options.refused()) return
    const original: BelvdrException = {
      ...occurrence.exception,
      frames: occurrence.exception.frames.map((frame) => ({ ...frame })),
      url: occurrence.url,
      timestamp: occurrence.timestamp,
    }
    let kept = { ...original, frames: original.frames }
    if (options.beforeSend) {
      let result: unknown
      try {
        result = options.beforeSend({ ...original, frames: original.frames.map((f) => ({ ...f })) })
      } catch (error) {
        warn("beforeSendException a levé une erreur : l'Exception n'est pas envoyée.", error)
        return
      }
      if (result === null) return
      if (typeof result !== 'object') {
        warn(
          "beforeSendException doit renvoyer l'Exception ou null : l'Exception n'est pas envoyée.",
        )
        return
      }
      kept = { ...original, ...rewritten(result as BelvdrException, original) }
    }
    const extraitId = occurrence.session ? undefined : options.extraitFor?.(occurrence)
    const report: ExceptionReport = {
      publicKey: options.publicKey,
      sdkVersion: SDK_VERSION,
      distinctId: occurrence.distinctId,
      uuid: uuidv7(occurrence.timestamp),
      timestamp: occurrence.timestamp,
      url: cleanPageUrl(kept.url),
      context: options.context(),
      type: kept.type,
      // `maskConsole` masque aussi le message des Exceptions (le type et la pile restent).
      message: options.maskMessage ? MASKED_MESSAGE : kept.message,
      frames: kept.frames.map(cleanFrame),
      handled: occurrence.exception.handled,
      mechanism: occurrence.exception.mechanism,
      ...(occurrence.userId === undefined ? {} : { userId: occurrence.userId }),
      ...(occurrence.session ? { session: occurrence.session } : {}),
      ...(extraitId === undefined ? {} : { extraitId }),
      ...(duplicateCount > 0 ? { duplicateCount } : {}),
    }
    // Page en train de se fermer : l'envoi doit lui survivre.
    options.sender.send(report, {
      keepalive: document.visibilityState === 'hidden',
      ...(extraitId === undefined
        ? {}
        : {
            onSettled: (response: TransportResponse | null) =>
              options.decideExtrait?.(extraitId, extraitDecision(response)),
          }),
    })
  }

  const deduplicator = createDeduplicator(send)

  return {
    report(occurrence) {
      const { type, message, frames, mechanism } = occurrence.exception
      deduplicator.offer(JSON.stringify([type, message, frames, mechanism]), occurrence)
    },
    flush: () => deduplicator.flush(),
  }
}
